import Glider from "glider-js"
import { useCallback, useEffect, useRef, useState } from "react"
import cn from "classnames"

import styles from "./styles.module.scss"
import { AvatarList } from "./components/AvatarList"
import { SETTINGS } from "./utils/constants"

import { Icon } from "components"
import { Psychologist } from "utils/interfaces"

interface Props {
    activePsy: Psychologist
    psyProfiles: Psychologist[]
    searchParams: URLSearchParams | Map<string, string | null>
}

export const ProfileStoriesNav = ({ psyProfiles, activePsy, searchParams }: Props) => {
    const listRef = useRef<HTMLDivElement | null>(null)
    const wrapperRef = useRef<HTMLDivElement | null>(null)
    const gliderRef = useRef<Glider | null>(null)
    const arrowLeftRef = useRef<HTMLDivElement | null>(null)
    const arrowRightRef = useRef<HTMLDivElement | null>(null)

    const initialIndex = psyProfiles.findIndex((psy) => psy.hash_id === activePsy.hash_id)
    const [sliderPosition, setSliderPosition] = useState(initialIndex)
    const [isSticky, setIsSticky] = useState(false)

    const onSlideVisible = useCallback(
        (event: Glider.GliderEvent<Glider.GliderEventMap["glider-slide-visible"]>) => {
            setSliderPosition(event.detail.slide)
        },
        []
    )

    useEffect(() => {
        const handleScroll = () => {
            if (!wrapperRef.current) return
            const top = wrapperRef.current.getBoundingClientRect().top
            setIsSticky(top === 0)
        }

        window.addEventListener("scroll", handleScroll)
        return () => window.removeEventListener("scroll", handleScroll)
    }, [])

    useEffect(() => {
        const left = arrowLeftRef.current
        const right = arrowRightRef.current

        const preventSelection = (e: MouseEvent) => {
            if (window.getSelection) {
                const selection = window.getSelection()
                if (selection && !selection.isCollapsed) {
                    selection.removeAllRanges()
                }
            }

            e.preventDefault()
        }

        left?.addEventListener("mousedown", preventSelection)
        right?.addEventListener("mousedown", preventSelection)

        return () => {
            left?.removeEventListener("mousedown", preventSelection)
            right?.removeEventListener("mousedown", preventSelection)
        }
    }, [])

    useEffect(() => {
        const listEl = listRef.current
        if (!listEl) return

        gliderRef.current = new Glider(listEl, {
            arrows: {
                prev: arrowLeftRef.current,
                next: arrowRightRef.current,
            },
            ...SETTINGS,
        })
        if (initialIndex > 0) {
            const listEl = listRef.current
            if (!listEl) return

            const track = listEl.querySelector(".glider-track") as HTMLElement
            if (!track) return

            const items = track.children
            const targetItem = items[initialIndex] as HTMLElement
            if (!targetItem) return

            requestAnimationFrame(() => {
                listEl.scrollTo({
                    left: targetItem.offsetLeft,
                    behavior: "smooth",
                })
            })
        }

        listEl.addEventListener("glider-slide-visible", onSlideVisible)

        return () => {
            listEl.removeEventListener("glider-slide-visible", onSlideVisible)
            gliderRef.current?.destroy()
        }
    }, [onSlideVisible])

    return (
        <div
            ref={wrapperRef}
            className={cn(styles.wrapper, {
                [styles.isSticky]: isSticky,
            })}
        >
            <div className={styles.container}>
                <div
                    ref={arrowLeftRef}
                    className={cn(styles.arrow, {
                        [styles.isActive]: sliderPosition > 0,
                    })}
                >
                    <Icon type="chevron-left" size={24} />
                </div>
                <div className={styles.inner}>
                    <AvatarList
                        ref={listRef}
                        psyProfiles={psyProfiles}
                        activePsy={activePsy}
                        searchParams={searchParams}
                    />
                </div>
                <div
                    ref={arrowRightRef}
                    className={cn(styles.arrow, {
                        [styles.isActive]: sliderPosition < psyProfiles.length - 1,
                    })}
                    onClick={(event) => event.stopPropagation()}
                >
                    <Icon type="chevron-right" size={24} />
                </div>
            </div>
        </div>
    )
}
